import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  MenuItem,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import { formatDateText } from "../CommonFunctions";

export default function RenewForm({
  open,
  onClose,
  tenancyDetails,
  handleRenew
}) {
  const [rentalType, setRentalType] = useState("weekly");
  const [rate, setRate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const rentalTypes = ["daily", "weekly", "monthly"];

  // 🟦 Helper to compute new end date based on rental type
  const computeEndDate = (start, type) => {
    if (!start) return "";

    let startDate = new Date(start);

    switch (type) {
      case "daily":
        return format(addDays(startDate,2), "yyyy-MM-dd");

      case "weekly":
        return format(addDays(startDate,8), "yyyy-MM-dd");

      case "monthly":
        return format(addDays(startDate, 31), "yyyy-MM-dd"); // simple rule
      default:
        return start;
    }
  };

  // 🟦 Load defaults on mount
  useEffect(() => {
    if (tenancyDetails) {
      setRate(tenancyDetails.rate);

      const lastCheckout = tenancyDetails.checkout;
      if (lastCheckout) {
        setFromDate(lastCheckout);

        const computedEnd = computeEndDate(lastCheckout, rentalType);
        setToDate(computedEnd);
      }
    }
  }, [tenancyDetails]);

  // 🟦 Update end date when rentalType changes
  useEffect(() => {
    if (fromDate) {
      const newEnd = computeEndDate(fromDate, rentalType);
      setToDate(newEnd);
    }
  }, [rentalType]);

  // Update end date when fromDate is changed manually
  useEffect(() => {
    if (fromDate) {
      const newEnd = computeEndDate(fromDate, rentalType);
      setToDate(newEnd);
    }
  }, [fromDate]);

  const onRenew = () => {
    handleRenew({
      rentalType,
      rate,
      fromDate,
      toDate
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Renew Tenancy
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>

          {/* Rental Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Rental Type</InputLabel>
              <Select
                label="Rental Type"
                value={rentalType}
                onChange={(e) => setRentalType(e.target.value)}
              >
                {rentalTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Rent */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Rent"
              fullWidth
              size="small"
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </Grid>

          

          {/* Info Box */}
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                bgcolor: "#e8f4ff",
                color: "#004a8f"
              }}
              action={
                <IconButton size="small" color="inherit">
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              Tenancy will be renewed from <b>{formatDateText(fromDate)}</b>  
              {" "}to{" "}
              <b>{formatDateText(toDate)}</b> based on the selected rental type.
            </Alert>
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", px: 3 }} color="secondary">
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={onRenew}
          sx={{ textTransform: "none", px: 4, bgcolor: "#0066ff" }}
        >
          Yes, Renew
        </Button>
      </DialogActions>
    </Dialog>
  );
}
